import { Either, Schema } from "effect";

/**
 * The v3 loop document — the graph, agent or deterministic action a loop
 * definition may carry beside its templates (PLAN-LOOP-MODEL-V3 §9).
 *
 * **There is deliberately no interior schema here.** The canonical v3 grammar —
 * nodes, evaluators, routes, watches, pools, fan-out — is `@fm/loops`'
 * `document/schema.ts`, and restating it in `@fm/db` would be the second
 * declaration this repo's "never duplicate a model type" rule exists to
 * prevent: the two would drift on the first added key. So `@fm/db` owns the
 * *column* and the *gate*, never the grammar. The document is modelled as a
 * plain JSON object here, structurally opaque, and the real validator arrives
 * later as a {@link LoopDocumentValidator} implemented in `@fm/loops` and passed
 * to the accessor — no import of `@fm/loops` from this package.
 *
 * @since 1.0.0
 */
export const LoopDocument = Schema.Record({ key: Schema.String, value: Schema.Unknown });
export type LoopDocument = Schema.Schema.Type<typeof LoopDocument>;

/**
 * A validator's refusal to admit a document.
 *
 * `rule` is the validator's own rule id (see {@link LOOP_DOCUMENT_RULES}) and is
 * what a caller shows a person; `path` is a JSON pointer into the offending part
 * of the document, or `null` when the whole document is at fault. `path` is
 * `NullOr`, never optional — the same reasoning `LoopInput` states for its
 * nullable fields: the value may round-trip through the wire, and an absent key
 * and an explicit `null` must not be two different states.
 *
 * @since 1.0.0
 */
export const LoopDocumentRefusal = Schema.Struct({
  rule: Schema.String,
  message: Schema.String,
  path: Schema.NullOr(Schema.String),
});
export type LoopDocumentRefusal = Schema.Schema.Type<typeof LoopDocumentRefusal>;

/**
 * The interface `@fm/loops`' real document validator will satisfy — a plain
 * structural TypeScript interface, so `@fm/db` never imports `@fm/loops`.
 *
 * `validate` returns the document itself on the right (so validators compose)
 * or a {@link LoopDocumentRefusal} on the left.
 *
 * @since 1.0.0
 */
export interface LoopDocumentValidator {
  readonly name: string;
  readonly validate: (document: LoopDocument) => Either.Either<LoopDocument, LoopDocumentRefusal>;
}

/**
 * The three rule ids `@fm/db` can defend on its own evidence, named so tests and
 * callers reference them rather than spelling the strings.
 *
 * There is deliberately NO rule that guesses at v3 grammar (a required `nodes`
 * key, a required `@version`): a v3 loop may be a graph, an agent or a
 * deterministic action, so none of those is universal, and inventing one here
 * would silently refuse valid documents. That is `@fm/loops`' job.
 *
 * @since 1.0.0
 */
export const LOOP_DOCUMENT_RULES = {
  /** The body is a JSON object — an array, string, number, boolean or null is refused. */
  isObject: "document.is-object",
  /** The body survives a `JSON.parse(JSON.stringify(body))` round-trip unchanged. */
  isJson: "document.is-json",
  /** The encoded body is at most {@link LOOP_DOCUMENT_MAX_BYTES}. */
  withinSize: "document.within-size",
} as const;

/**
 * The ceiling on an encoded document (256 KiB). `loop_versions` freezes a copy
 * of the body on every content change, so an unbounded column is unbounded write
 * amplification as well as a denial-of-service surface.
 *
 * @since 1.0.0
 */
export const LOOP_DOCUMENT_MAX_BYTES = 256 * 1024;

/**
 * Does `value` survive a jsonb round-trip byte-for-byte? A value carrying
 * `undefined`, a function, a `symbol`, a `BigInt`, a non-finite number or a
 * cycle does NOT: `JSON.stringify` drops, nulls out or throws on those, so the
 * stored row would differ from what the caller handed in — a corrupt row. Walked
 * by hand (rather than via a `try`/`catch` around `JSON.stringify`, which Effect
 * lint forbids), tracking visited objects so a cycle is caught rather than
 * recursed forever.
 */
const survivesJsonRoundTrip = (value: unknown, seen: ReadonlySet<object> = new Set()): boolean => {
  if (value === null) return true;
  const kind = typeof value;
  if (kind === "string" || kind === "boolean") return true;
  if (kind === "number") return Number.isFinite(value);
  // undefined, bigint, function and symbol never survive jsonb.
  if (kind !== "object") return false;
  const object = value as object;
  if (seen.has(object)) return false;
  const next = new Set(seen).add(object);
  return Array.isArray(object)
    ? object.every((item) => survivesJsonRoundTrip(item, next))
    : Object.values(object as Record<string, unknown>).every((item) => survivesJsonRoundTrip(item, next));
};

/** The three structural rules, applied in the order {@link LOOP_DOCUMENT_RULES} lists them. */
const structuralRules: ReadonlyArray<(value: unknown) => LoopDocumentRefusal | null> = [
  (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value)
      ? null
      : { rule: LOOP_DOCUMENT_RULES.isObject, message: "a loop document must be a JSON object", path: null },
  (value) =>
    survivesJsonRoundTrip(value)
      ? null
      : {
        rule: LOOP_DOCUMENT_RULES.isJson,
        message: "a loop document must survive a jsonb round-trip unchanged",
        path: null,
      },
  (value) =>
    new TextEncoder().encode(JSON.stringify(value)).length <= LOOP_DOCUMENT_MAX_BYTES
      ? null
      : {
        rule: LOOP_DOCUMENT_RULES.withinSize,
        message: `a loop document must be at most ${LOOP_DOCUMENT_MAX_BYTES} bytes`,
        path: null,
      },
];

/**
 * The validator `@fm/db` ships today: three rules honestly its own business — it
 * is a JSON object, it survives a jsonb round-trip, it is not unbounded —
 * applied in order and returning the FIRST refusal. It guesses at no v3 key.
 *
 * @since 1.0.0
 */
export const structuralLoopDocumentValidator: LoopDocumentValidator = {
  name: "structural",
  validate: (document) => {
    const refusal = structuralRules.reduce<LoopDocumentRefusal | null>(
      (found, rule) => (found === null ? rule(document) : found),
      null,
    );
    return refusal === null ? Either.right(document) : Either.left(refusal);
  },
};

/**
 * Runs a series of validators left to right, short-circuiting on the first
 * refusal; `name` is the members' names joined with `"+"`. This is the seam
 * `@fm/loops`' grammar validator plugs into without editing anything here — the
 * structural rules keep running and the grammar rules run after them.
 *
 * @since 1.0.0
 */
export const composeLoopDocumentValidators = (
  ...validators: ReadonlyArray<LoopDocumentValidator>
): LoopDocumentValidator => ({
  name: validators.map((validator) => validator.name).join("+"),
  validate: (document) =>
    validators.reduce<Either.Either<LoopDocument, LoopDocumentRefusal>>(
      (accumulated, validator) => Either.flatMap(accumulated, () => validator.validate(document)),
      Either.right(document),
    ),
});

/**
 * The ONE gate a body passes through on its way to a row. An absent body and an
 * explicit `null` both pass untouched — clearing a document is legal, and a
 * patch that does not mention `body` must not be forced to supply one; only a
 * present document is validated.
 *
 * @since 1.0.0
 */
export const validateLoopDocumentForAppend = (
  validator: LoopDocumentValidator,
  body: LoopDocument | null | undefined,
): Either.Either<LoopDocument | null | undefined, LoopDocumentRefusal> =>
  body === null || body === undefined ? Either.right(body) : validator.validate(body);

/**
 * The one spelling of a refusal as prose, so the accessor's backstop message and
 * any caller that parses or asserts on it agree in one place rather than two.
 *
 * @since 1.0.0
 */
export const LOOP_DOCUMENT_REFUSAL_PREFIX = "loop document refused by rule";

/**
 * Renders a refusal as `"loop document refused by rule <rule>: <message>"`.
 *
 * @since 1.0.0
 */
export const formatLoopDocumentRefusal = (refusal: LoopDocumentRefusal): string =>
  `${LOOP_DOCUMENT_REFUSAL_PREFIX} ${refusal.rule}: ${refusal.message}`;
